import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Input, Select } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar } from "../../../../utils/billingMoney";
import { grantSubscriptionAPI, listBillingPlansAPI, listBillingStoresAPI } from "../../../../utils/api/billing";
import { grantText } from "../grantText";

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `grant-${Date.now()}-${Math.random().toString(16).slice(2)}`);

/*
  Activate a Store's subscription WITHOUT payment (ceo / cto / developer). Two steps: fill in, then an explicit confirmation that states what
  will happen. One idempotency key per opened modal, so a double click or a retry cannot grant twice. The server re-checks everything
  (role, dates, branches, conflicts); nothing here is trusted.
*/
const GrantSubscriptionModal = ({ initialStoreId = null, onCancel, onGranted }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const t = grantText(lang);
  const token = localStorage.getItem("auth-token");
  const idempotencyKey = useRef(newKey());
  const [stores, setStores] = useState([]);
  const [plans, setPlans] = useState([]);
  const [storeId, setStoreId] = useState(initialStoreId || "");
  const [planId, setPlanId] = useState("");
  const [branchIds, setBranchIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [step, setStep] = useState("form");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [storeResult, planResult] = await Promise.all([listBillingStoresAPI(token), listBillingPlansAPI(token, { status: "active", limit: 100 })]);
        setStores(storeResult.data || []);
        setPlans((planResult.data || []).filter((plan) => plan.type === "platform"));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const store = useMemo(() => stores.find((item) => item.id === storeId), [stores, storeId]);
  const plan = useMemo(() => plans.find((item) => item._id === planId), [plans, planId]);
  const complete = Boolean(storeId && planId && branchIds.length && startDate && endDate && reason.trim());

  const review = () => {
    if (!complete) return setError(t.needAll);
    if (endDate <= startDate) return setError(t.endBeforeStart);
    setError("");
    setStep("confirm");
  };

  const submit = async () => {
    // handler-level authorization: the server refuses anyone else as well
    if (!can("billing.subscription.grant") || saving) return;
    setSaving(true);
    setError("");
    try {
      await grantSubscriptionAPI(token, { storeId, planId, branchIds, startDate, endDate, reason: reason.trim(), confirm: true, idempotencyKey: idempotencyKey.current });
      await onGranted();
    } catch (err) {
      setError(err.message);
      setSaving(false);
      setStep("form");
    }
  };

  const toggleBranch = (branchId) => setBranchIds((current) => (current.includes(branchId) ? current.filter((id) => id !== branchId) : [...current, branchId]));
  const chosenBranches = (store?.branches || []).filter((branch) => branchIds.includes(branch.branchId));

  return (
    <div className="billing-modal-backdrop" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="billing-modal" role="dialog" aria-modal="true" aria-label={t.title}>
        {step === "form" ? (
          <>
            <h3>{t.title}</h3>
            <p>{t.description}</p>
            {error && <div className="billing-error" role="alert">{error}</div>}
            {loading ? (
              <div className="billing-loading">…</div>
            ) : (
              <div className="billing-plan-modal-form">
                {initialStoreId ? (
                  <div className="billing-preview-box">
                    <span>{t.store}</span>
                    <strong>{store?.businessName || initialStoreId}</strong>
                  </div>
                ) : (
                  <Select
                    label={t.store}
                    value={storeId}
                    options={stores.map((item) => ({ value: item.id, label: `${item.businessName || item.id} (${item.id})` }))}
                    onChange={(event) => {
                      setStoreId(event.target.value);
                      setBranchIds([]);
                    }}
                    placeholder={t.selectStore}
                  />
                )}
                <Select
                  label={t.plan}
                  value={planId}
                  options={plans.map((item) => ({ value: item._id, label: `${item.code} v${item.version} - ${item.nameEn} - ${formatSar(item.basePriceMinor, lang)}` }))}
                  onChange={(event) => setPlanId(event.target.value)}
                  placeholder={t.selectPlan}
                />
                <div className="billing-form-grid">
                  <Input type="date" label={t.startDate} required value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                  <Input type="date" label={t.endDate} required min={startDate || undefined} value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                </div>
                {store && (
                  <div className="billing-branch-list">
                    <strong>{t.branches}</strong>
                    {(store.branches || []).map((branch) => (
                      <label key={branch.branchId} className="billing-branch-option">
                        <input type="checkbox" checked={branchIds.includes(branch.branchId)} onChange={() => toggleBranch(branch.branchId)} />
                        <span>{branch.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                <Input textarea label={t.reason} required value={reason} onChange={(event) => setReason(event.target.value)} />
              </div>
            )}
            <div className="billing-modal-actions">
              <Button appearance="outline" onClick={onCancel}>{t.cancel}</Button>
              <Button appearance="filled" disabled={loading || !complete} onClick={review}>{t.review}</Button>
            </div>
          </>
        ) : (
          <>
            <h3>{t.confirmTitle}</h3>
            <p>{t.confirmBody}</p>
            <div className="billing-preview-box">
              <div><span>{t.store}: </span><strong>{store?.businessName || storeId} ({storeId})</strong></div>
              <div><span>{t.plan}: </span><strong>{plan?.code} v{plan?.version}</strong></div>
              <div><span>{t.period}: </span><strong dir="ltr">{startDate} → {endDate}</strong></div>
              <div><span>{t.branches}: </span><strong>{chosenBranches.map((branch) => branch.name).join("، ")}</strong></div>
              <div><span>{t.reason.replace(/ \(.*\)$/, "")}: </span><strong>{reason.trim()}</strong></div>
            </div>
            {error && <div className="billing-error" role="alert">{error}</div>}
            <div className="billing-modal-actions">
              <Button appearance="outline" disabled={saving} onClick={() => setStep("form")}>{t.back}</Button>
              <Button appearance="filled" disabled={saving} onClick={submit}>{saving ? t.saving : t.confirm}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GrantSubscriptionModal;
